import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import manualPaymentVerificationService from '../../../services/manualPaymentVerificationService';
import { rbacService } from '../../../services/rbacService';

const AccessManagementPanel = () => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [grantingAccess, setGrantingAccess] = useState(false);
  const [accessForm, setAccessForm] = useState({
    companyId: '',
    userEmail: '',
    roleId: '',
    department: '',
    permissions: []
  });

  useEffect(() => {
    loadRoles();
  }, []);

  const loadRoles = async () => {
    try {
      setLoading(true);
      setError('');
      const rolesData = await rbacService?.getAllRoles();
      setRoles(rolesData?.filter(r => r?.isActive) || []);
    } catch (err) {
      setError(err?.message || 'Failed to load roles');
    } finally {
      setLoading(false);
    }
  };

  const handleGrantAccess = async () => {
    try {
      setGrantingAccess(true);
      setError('');

      if (!accessForm?.userEmail || !accessForm?.roleId) {
        throw new Error('User email and role are required');
      }

      await manualPaymentVerificationService?.grantPortalAccess(accessForm);

      // Reset form
      setAccessForm({
        companyId: '',
        userEmail: '',
        roleId: '',
        department: '',
        permissions: []
      });

      alert('Portal access granted successfully!');
    } catch (err) {
      setError(err?.message || 'Failed to grant access');
    } finally {
      setGrantingAccess(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-muted/50 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-4">
          <Icon name="Shield" className="w-5 h-5 text-primary" />
          <h3 className="text-lg font-semibold text-foreground">Grant Portal Access</h3>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          Manually grant portal access to client users with specific role assignments and permissions
        </p>

        {error && (
          <div className="bg-error/10 border border-error/20 rounded-lg p-3 mb-4">
            <p className="text-sm text-error">{error}</p>
          </div>
        )}

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">User Email</label>
            <Input
              type="email"
              placeholder="user@company.com"
              value={accessForm?.userEmail}
              onChange={(e) => setAccessForm({ ...accessForm, userEmail: e?.target?.value })}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Role Template</label>
            <Select
              value={accessForm?.roleId}
              onChange={(e) => setAccessForm({ ...accessForm, roleId: e?.target?.value })}
            >
              <option value="">Select a role...</option>
              {roles?.map((role) => (
                <option key={role?.id} value={role?.id}>
                  {role?.roleName} - {role?.roleDescription}
                </option>
              ))}
            </Select>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Department (Optional)</label>
            <Input
              type="text"
              placeholder="e.g., HR, Finance, Operations"
              value={accessForm?.department}
              onChange={(e) => setAccessForm({ ...accessForm, department: e?.target?.value })}
            />
          </div>

          <Button
            onClick={handleGrantAccess}
            disabled={grantingAccess || !accessForm?.userEmail || !accessForm?.roleId}
            className="w-full"
          >
            {grantingAccess ? (
              <>
                <Icon name="Loader2" size={16} className="mr-2 animate-spin" />
                Granting Access...
              </>
            ) : (
              <>
                <Icon name="Shield" size={16} className="mr-2" />
                Grant Portal Access
              </>
            )}
          </Button>
        </div>
      </div>
      {/* Role Templates Reference */}
      <div className="bg-card rounded-lg border border-border p-4">
        <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
          <Icon name="BookOpen" size={16} />
          Available Role Templates
        </h4>
        <div className="space-y-2">
          {loading ? (
            <div className="flex items-center justify-center py-4">
              <Icon name="Loader2" className="w-5 h-5 text-primary animate-spin" />
            </div>
          ) : roles?.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">No roles available</p>
          ) : (
            roles?.map((role) => (
              <div key={role?.id} className="border border-border rounded-lg p-3">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-foreground">{role?.roleName}</p>
                    <p className="text-xs text-muted-foreground mt-1">{role?.roleDescription}</p>
                  </div>
                  {role?.isSystemRole && (
                    <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-bold rounded">
                      SYSTEM
                    </span>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default AccessManagementPanel;